/**
 * Overleggsmusikk for reise-replay (CapCut-inspirert stemning, eier-ønske).
 *
 * LISENS-TRYGT BY DESIGN: sporene KOMPONERES og SYNTETISERES i appen
 * (OfflineAudioContext) — ingen innspilte låter, ingen opphavsrett, ingen
 * Content-ID-treff når videoen deles. Åtte stemninger; de rytmiske har
 * syntetiserte trommer (kick/hihat/snare) og bass for sosiale-medier-energi.
 * (Ekte lisensiert låtbibliotek à la TikTok krever avtaler — bevisst utelatt.)
 *
 *   buildMusicScore (PURE, testet) ──► renderMusicBuffer (OfflineAudioContext)
 *        │                                   │
 *        └── deterministisk noteliste        └──► encodeAacIntoMuxer (WebCodecs
 *                                                  AudioEncoder → mp4-muxer)
 *
 * GIF har naturlig ingen lyd; MediaRecorder-fallbacken hopper over musikk.
 */

export type MusicStyle =
  | 'calm' | 'warm' | 'arctic'          // stemningsflater (klippestilenes default)
  | 'upbeat' | 'roadtrip' | 'coast'     // rytmiske
  | 'epic' | 'lofi';

export const ALL_MUSIC_STYLES: MusicStyle[] = [
  'calm', 'warm', 'arctic', 'upbeat', 'roadtrip', 'coast', 'epic', 'lofi',
];

export type NoteKind = 'tone' | 'kick' | 'hihat' | 'snare';

export interface MusicNote {
  startSec: number;
  durSec: number;
  freq: number;   // ignoreres for trommer (kick har egen pitch-envelope)
  gain: number;
  wave: OscillatorType;
  kind: NoteKind;
}

/** Grunnfrekvenser (Hz). */
const NOTE = {
  C2: 65.41, D2: 73.42, E2: 82.41, F2: 87.31, G2: 98.0, A2: 110.0, Bb2: 116.54, B2: 123.47,
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, Bb3: 233.08, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, Bb4: 466.16, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.26, F5: 698.46, G5: 783.99, A5: 880.0,
};

/** Tromme-slag innen en takt: åttedel-indeks 0–7. */
interface DrumHit { eighth: number; kind: Exclude<NoteKind, 'tone'>; gain: number }

interface StyleSpec {
  barSec: number;
  padWave: OscillatorType;
  padGain: number;
  arpWave: OscillatorType;
  arpGain: number;
  /** Master-lavpass (Hz) — rytmiske stiler trenger mer topp. */
  brightness: number;
  chords: number[][];
  arps: number[][];
  /** Bass: grunntoner per akkord (spilles i åttedels-mønster hvis satt). */
  bass?: number[];
  bassPattern?: number[]; // åttedeler der bassen slår (default [0,4])
  drums?: DrumHit[];
  /** Swing: forsinkelse (andel av åttedel) på odde åttedeler (lofi). */
  swing?: number;
}

const bpmToBar = (bpm: number) => (4 * 60) / bpm;

const STYLE_SPECS: Record<MusicStyle, StyleSpec> = {
  // ── Stemningsflater ────────────────────────────────────────────────────────
  // 8-takters progresjoner (eier-funn: for lite variasjon) — dobbelt så lang
  // harmonisk sløyfe før den gjentar seg, så bakgrunnsmusikken føles mindre loop.
  calm: {
    barSec: 3.2, padWave: 'sine', padGain: 0.16, arpWave: 'triangle', arpGain: 0.07,
    brightness: 2400,
    chords: [
      [NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.F3, NOTE.A3, NOTE.C4],
      [NOTE.C3, NOTE.E3, NOTE.G3, NOTE.C4], [NOTE.G3, NOTE.B3, NOTE.D4],
      [NOTE.D3, NOTE.F3, NOTE.A3], [NOTE.A3, NOTE.C4, NOTE.E4],
      [NOTE.E3, NOTE.G3, NOTE.B3], [NOTE.G3, NOTE.B3, NOTE.D4],
    ],
    arps: [
      [NOTE.E5, NOTE.C5, NOTE.A4, NOTE.C5], [NOTE.C5, NOTE.A4, NOTE.F4, NOTE.A4],
      [NOTE.G4, NOTE.E4, NOTE.C4, NOTE.E4], [NOTE.D5, NOTE.B4, NOTE.G4, NOTE.B4],
      [NOTE.F5, NOTE.D5, NOTE.A4, NOTE.D5], [NOTE.E5, NOTE.C5, NOTE.A4, NOTE.C5],
      [NOTE.B4, NOTE.G4, NOTE.E4, NOTE.G4], [NOTE.D5, NOTE.B4, NOTE.G4, NOTE.B4],
    ],
  },
  warm: {
    barSec: 2.8, padWave: 'sawtooth', padGain: 0.055, arpWave: 'sine', arpGain: 0.08,
    brightness: 2600,
    chords: [
      [NOTE.C3, NOTE.E3, NOTE.G3], [NOTE.A3, NOTE.C4, NOTE.E4],
      [NOTE.F3, NOTE.A3, NOTE.C4], [NOTE.G3, NOTE.B3, NOTE.D4],
      [NOTE.E3, NOTE.G3, NOTE.B3], [NOTE.A3, NOTE.C4, NOTE.E4],
      [NOTE.D3, NOTE.F3, NOTE.A3], [NOTE.G3, NOTE.B3, NOTE.D4],
    ],
    arps: [
      [NOTE.C5, NOTE.E5, NOTE.G4, NOTE.E5], [NOTE.A4, NOTE.C5, NOTE.E5, NOTE.C5],
      [NOTE.F4, NOTE.A4, NOTE.C5, NOTE.A4], [NOTE.G4, NOTE.B4, NOTE.D5, NOTE.B4],
      [NOTE.E5, NOTE.G4, NOTE.B4, NOTE.G4], [NOTE.A4, NOTE.C5, NOTE.E5, NOTE.C5],
      [NOTE.D5, NOTE.F4, NOTE.A4, NOTE.F4], [NOTE.G4, NOTE.B4, NOTE.D5, NOTE.B4],
    ],
  },
  arctic: {
    barSec: 4.0, padWave: 'sine', padGain: 0.18, arpWave: 'sine', arpGain: 0.045,
    brightness: 2200,
    chords: [
      [NOTE.D3, NOTE.F3, NOTE.A3], [NOTE.Bb3, NOTE.D4, NOTE.F4],
      [NOTE.F3, NOTE.A3, NOTE.C4], [NOTE.C3, NOTE.G3, NOTE.E4],
      [NOTE.G3, NOTE.Bb3, NOTE.D4], [NOTE.F3, NOTE.A3, NOTE.C4],
      [NOTE.Bb3, NOTE.D4, NOTE.F4], [NOTE.C3, NOTE.E3, NOTE.G3],
    ],
    arps: [
      [NOTE.A4, NOTE.D5], [NOTE.F5, NOTE.D5], [NOTE.C5, NOTE.A4], [NOTE.G4, NOTE.E5],
      [NOTE.Bb4, NOTE.G4], [NOTE.C5, NOTE.A4], [NOTE.F5, NOTE.D5], [NOTE.G4, NOTE.E5],
    ],
  },

  // ── Rytmiske (TikTok-aktig energi — syntetisert, lisensfri) ───────────────
  upbeat: {
    barSec: bpmToBar(118), padWave: 'sawtooth', padGain: 0.045,
    arpWave: 'square', arpGain: 0.05, brightness: 7500,
    chords: [
      [NOTE.C3, NOTE.E3, NOTE.G3], [NOTE.G3, NOTE.B3, NOTE.D4],
      [NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.F3, NOTE.A3, NOTE.C4],
    ],
    arps: [
      [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.E5], [NOTE.D5, NOTE.G4, NOTE.B4, NOTE.G5],
      [NOTE.E5, NOTE.C5, NOTE.A4, NOTE.C5], [NOTE.C5, NOTE.A4, NOTE.F4, NOTE.A4],
    ],
    bass: [NOTE.C2, NOTE.G2, NOTE.A2, NOTE.F2],
    bassPattern: [0, 2, 3, 4, 6, 7],
    drums: [
      { eighth: 0, kind: 'kick', gain: 0.5 }, { eighth: 4, kind: 'kick', gain: 0.5 },
      { eighth: 2, kind: 'snare', gain: 0.28 }, { eighth: 6, kind: 'snare', gain: 0.28 },
      { eighth: 1, kind: 'hihat', gain: 0.14 }, { eighth: 3, kind: 'hihat', gain: 0.14 },
      { eighth: 5, kind: 'hihat', gain: 0.14 }, { eighth: 7, kind: 'hihat', gain: 0.14 },
    ],
  },
  roadtrip: {
    barSec: bpmToBar(100), padWave: 'triangle', padGain: 0.09,
    arpWave: 'triangle', arpGain: 0.09, brightness: 6000,
    chords: [
      [NOTE.D3, NOTE.A3, NOTE.D4], [NOTE.A3, NOTE.E4, NOTE.A4] ,
      [NOTE.B3, NOTE.D4, NOTE.F4], [NOTE.G3, NOTE.B3, NOTE.D4],
    ],
    arps: [
      [NOTE.D4, NOTE.A4, NOTE.D5, NOTE.A4], [NOTE.E4, NOTE.A4, NOTE.C5, NOTE.A4],
      [NOTE.D4, NOTE.B4, NOTE.D5, NOTE.B4], [NOTE.D4, NOTE.G4, NOTE.B4, NOTE.G4],
    ],
    bass: [NOTE.D2, NOTE.A2, NOTE.B2, NOTE.G2],
    bassPattern: [0, 3, 4],
    drums: [
      { eighth: 0, kind: 'kick', gain: 0.4 }, { eighth: 4, kind: 'kick', gain: 0.35 },
      { eighth: 2, kind: 'snare', gain: 0.2 }, { eighth: 6, kind: 'snare', gain: 0.22 },
      { eighth: 3, kind: 'hihat', gain: 0.1 }, { eighth: 7, kind: 'hihat', gain: 0.1 },
    ],
  },
  coast: {
    barSec: bpmToBar(95), padWave: 'sine', padGain: 0.12,
    arpWave: 'sine', arpGain: 0.1, brightness: 5000,
    chords: [
      [NOTE.C3, NOTE.E3, NOTE.G3], [NOTE.F3, NOTE.A3, NOTE.C4],
      [NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.G3, NOTE.B3, NOTE.D4],
    ],
    arps: [
      [NOTE.G4, NOTE.C5, NOTE.E5, NOTE.C5], [NOTE.A4, NOTE.C5, NOTE.F5, NOTE.C5],
      [NOTE.E4, NOTE.A4, NOTE.C5, NOTE.A4], [NOTE.D4, NOTE.G4, NOTE.B4, NOTE.G4],
    ],
    bass: [NOTE.C2, NOTE.F2, NOTE.A2, NOTE.G2],
    bassPattern: [0, 4, 6],
    drums: [
      { eighth: 0, kind: 'kick', gain: 0.3 },
      { eighth: 4, kind: 'snare', gain: 0.15 },
      { eighth: 2, kind: 'hihat', gain: 0.08 }, { eighth: 6, kind: 'hihat', gain: 0.08 },
    ],
  },
  epic: {
    barSec: bpmToBar(75), padWave: 'sawtooth', padGain: 0.06,
    arpWave: 'sine', arpGain: 0.06, brightness: 4500,
    chords: [
      [NOTE.A2, NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.F2, NOTE.F3, NOTE.A3, NOTE.C4],
      [NOTE.C3, NOTE.C4, NOTE.E4, NOTE.G4], [NOTE.G2, NOTE.G3, NOTE.B3, NOTE.D4],
    ],
    arps: [
      [NOTE.E5, NOTE.A4, NOTE.C5, NOTE.E5], [NOTE.C5, NOTE.F4, NOTE.A4, NOTE.C5],
      [NOTE.G5, NOTE.C5, NOTE.E5, NOTE.G5], [NOTE.D5, NOTE.G4, NOTE.B4, NOTE.D5],
    ],
    bass: [NOTE.A2 / 2, NOTE.F2 / 2 * 2, NOTE.C2, NOTE.G2],
    bassPattern: [0],
    drums: [
      { eighth: 0, kind: 'kick', gain: 0.55 },
      { eighth: 4, kind: 'kick', gain: 0.3 },
      { eighth: 6, kind: 'snare', gain: 0.18 },
    ],
  },
  lofi: {
    barSec: bpmToBar(82), padWave: 'triangle', padGain: 0.13,
    arpWave: 'sine', arpGain: 0.07, brightness: 3200, swing: 0.28,
    chords: [
      [NOTE.F3, NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.D3, NOTE.F3, NOTE.A3, NOTE.C4],
      [NOTE.Bb3, NOTE.D4, NOTE.F4], [NOTE.C3, NOTE.E3, NOTE.G3, NOTE.Bb3],
    ],
    arps: [
      [NOTE.E4, NOTE.A4, NOTE.C5], [NOTE.C4, NOTE.F4, NOTE.A4],
      [NOTE.D4, NOTE.F4, NOTE.Bb4], [NOTE.E4, NOTE.G4, NOTE.Bb4],
    ],
    bass: [NOTE.F2, NOTE.D2, NOTE.Bb2, NOTE.C2],
    bassPattern: [0, 5],
    drums: [
      { eighth: 0, kind: 'kick', gain: 0.35 }, { eighth: 5, kind: 'kick', gain: 0.2 },
      { eighth: 2, kind: 'snare', gain: 0.16 }, { eighth: 6, kind: 'snare', gain: 0.16 },
      { eighth: 1, kind: 'hihat', gain: 0.07 }, { eighth: 3, kind: 'hihat', gain: 0.07 },
      { eighth: 5, kind: 'hihat', gain: 0.07 }, { eighth: 7, kind: 'hihat', gain: 0.07 },
    ],
  },
};

/**
 * PURE: bygger hele notelisten (pad + arp + bass + trommer) for et spor av
 * gitt lengde. Deterministisk — samme (style, totalSec) gir alltid samme
 * score. Fade ut håndteres i render (master-gain).
 */
export function buildMusicScore(style: MusicStyle, totalSec: number): MusicNote[] {
  const spec = STYLE_SPECS[style];
  const notes: MusicNote[] = [];
  if (totalSec <= 0) return notes;

  let t = 0;
  let bar = 0;
  while (t < totalSec) {
    const chord = spec.chords[bar % spec.chords.length];
    const arp = spec.arps[bar % spec.arps.length];
    const barDur = Math.min(spec.barSec, totalSec - t + 0.4);
    const stepDur = spec.barSec / 8;
    const swingOf = (i: number) => (spec.swing && i % 2 === 1 ? stepDur * spec.swing : 0);

    for (const freq of chord) {
      notes.push({ startSec: t, durSec: barDur + 0.35, freq, gain: spec.padGain, wave: spec.padWave, kind: 'tone' });
    }
    for (let i = 0; i < 8; i++) {
      const st = t + i * stepDur + swingOf(i);
      if (st >= totalSec) break;
      notes.push({
        startSec: st, durSec: stepDur * 0.9, freq: arp[i % arp.length],
        gain: spec.arpGain, wave: spec.arpWave, kind: 'tone',
      });
    }
    if (spec.bass) {
      const root = spec.bass[bar % spec.bass.length];
      for (const i of spec.bassPattern ?? [0, 4]) {
        const st = t + i * stepDur + swingOf(i);
        if (st >= totalSec) break;
        notes.push({ startSec: st, durSec: stepDur * 0.85, freq: root, gain: 0.16, wave: 'triangle', kind: 'tone' });
      }
    }
    for (const hit of spec.drums ?? []) {
      const st = t + hit.eighth * stepDur + swingOf(hit.eighth);
      if (st >= totalSec) continue;
      notes.push({ startSec: st, durSec: hit.kind === 'hihat' ? 0.06 : 0.22, freq: 0, gain: hit.gain, wave: 'sine', kind: hit.kind });
    }
    t += spec.barSec;
    bar++;
  }
  return notes;
}

const SAMPLE_RATE = 44100;
const FADE_OUT_SEC = 1.5;

/** Delt støy-buffer for hihat/snare (deterministisk LCG — ikke Math.random). */
function makeNoiseBuffer(ctx: OfflineAudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, SAMPLE_RATE, SAMPLE_RATE);
  const data = buf.getChannelData(0);
  let seed = 1234567;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    data[i] = (seed / 2147483648) - 1;
  }
  return buf;
}

/** Syntetiserer scoren til en stereo AudioBuffer (offline — ingen avspilling). */
export async function renderMusicBuffer(style: MusicStyle, totalSec: number): Promise<AudioBuffer> {
  const spec = STYLE_SPECS[style];
  const frames = Math.ceil(totalSec * SAMPLE_RATE);
  const ctx = new OfflineAudioContext(2, frames, SAMPLE_RATE);
  const noise = makeNoiseBuffer(ctx);

  const master = ctx.createGain();
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = spec.brightness;
  master.connect(lowpass).connect(ctx.destination);
  master.gain.setValueAtTime(0, 0);
  master.gain.linearRampToValueAtTime(1, Math.min(1, totalSec / 4));
  const fadeStart = Math.max(0, totalSec - FADE_OUT_SEC);
  master.gain.setValueAtTime(1, fadeStart);
  master.gain.linearRampToValueAtTime(0, totalSec);

  for (const n of buildMusicScore(style, totalSec)) {
    if (n.kind === 'tone') {
      const osc = ctx.createOscillator();
      osc.type = n.wave;
      osc.frequency.value = n.freq;
      const g = ctx.createGain();
      const attack = Math.min(0.4, n.durSec / 3);
      const release = Math.min(0.5, n.durSec / 3);
      g.gain.setValueAtTime(0, n.startSec);
      g.gain.linearRampToValueAtTime(n.gain, n.startSec + attack);
      g.gain.setValueAtTime(n.gain, Math.max(n.startSec + attack, n.startSec + n.durSec - release));
      g.gain.linearRampToValueAtTime(0, n.startSec + n.durSec);
      osc.connect(g).connect(master);
      osc.start(n.startSec);
      osc.stop(n.startSec + n.durSec + 0.05);
    } else if (n.kind === 'kick') {
      // Kick: sinus m/ pitch-envelope 150→40 Hz + rask gain-envelope.
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, n.startSec);
      osc.frequency.exponentialRampToValueAtTime(40, n.startSec + 0.12);
      const g = ctx.createGain();
      g.gain.setValueAtTime(n.gain, n.startSec);
      g.gain.exponentialRampToValueAtTime(0.001, n.startSec + n.durSec);
      osc.connect(g).connect(master);
      osc.start(n.startSec);
      osc.stop(n.startSec + n.durSec + 0.02);
    } else {
      // Hihat/snare: filtrert støy m/ kort envelope (+ liten tone i snaren).
      const src = ctx.createBufferSource();
      src.buffer = noise;
      const filter = ctx.createBiquadFilter();
      if (n.kind === 'hihat') {
        filter.type = 'highpass';
        filter.frequency.value = 6500;
      } else {
        filter.type = 'bandpass';
        filter.frequency.value = 1800;
        filter.Q.value = 0.8;
      }
      const g = ctx.createGain();
      g.gain.setValueAtTime(n.gain, n.startSec);
      g.gain.exponentialRampToValueAtTime(0.001, n.startSec + n.durSec);
      src.connect(filter).connect(g).connect(master);
      src.start(n.startSec, 0.1, n.durSec + 0.02);
      if (n.kind === 'snare') {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = 190;
        const og = ctx.createGain();
        og.gain.setValueAtTime(n.gain * 0.5, n.startSec);
        og.gain.exponentialRampToValueAtTime(0.001, n.startSec + 0.1);
        osc.connect(og).connect(master);
        osc.start(n.startSec);
        osc.stop(n.startSec + 0.12);
      }
    }
  }

  return ctx.startRendering();
}

/**
 * EGEN LYD (eier-ønske): dekoder brukerens lokale lydfil. Filen forlater aldri
 * enheten — den leses, tilpasses og bakes rett inn i eksportfila. Lisensansvar
 * ligger hos brukeren (egen fil), i motsetning til et innebygd låtbibliotek.
 */
export async function decodeUserAudio(file: File): Promise<AudioBuffer> {
  const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
  try {
    return await ctx.decodeAudioData(await file.arrayBuffer());
  } finally {
    void ctx.close().catch(() => {});
  }
}

/**
 * Dekoder en lydfil fra en URL (ferdige spor buntet i appen, eller et
 * strømme-/nedlastings-endepunkt). Samme rolle som decodeUserAudio, men henter
 * bytene selv. AudioContext lukkes uansett utfall.
 */
export async function decodeAudioFromUrl(url: string): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Kunne ikke hente lyd (HTTP ${res.status})`);
  const bytes = await res.arrayBuffer();
  const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
  try {
    return await ctx.decodeAudioData(bytes);
  } finally {
    void ctx.close().catch(() => {});
  }
}

/**
 * PURE: pen visningstittel fra et filnavn («cinematic-road-trip_02.mp3» →
 * «Cinematic Road Trip 02»). Fjerner extension + sti, bytter -/_ mot mellomrom,
 * kollapser mellomrom og stor forbokstav per ord.
 */
export function prettifyTrackTitle(fileOrPath: string): string {
  const base = fileOrPath.split('/').pop() || fileOrPath;
  const noExt = base.replace(/\.[a-z0-9]+$/i, '');
  const words = noExt.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!words) return 'Spor';
  return words.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Tilpasser lyd til sporlengden: looper hvis den er kortere, kutter hvis den
 * er lengre — alltid med fade inn og fade ut. Offline, deterministisk.
 */
export async function fitAudioToDuration(src: AudioBuffer, totalSec: number): Promise<AudioBuffer> {
  const frames = Math.ceil(totalSec * SAMPLE_RATE);
  const ctx = new OfflineAudioContext(2, frames, SAMPLE_RATE);
  const master = ctx.createGain();
  master.connect(ctx.destination);
  master.gain.setValueAtTime(0, 0);
  master.gain.linearRampToValueAtTime(1, Math.min(0.8, totalSec / 6));
  const fadeStart = Math.max(0, totalSec - FADE_OUT_SEC);
  master.gain.setValueAtTime(1, fadeStart);
  master.gain.linearRampToValueAtTime(0, totalSec);

  const node = ctx.createBufferSource();
  node.buffer = src;
  node.loop = src.duration < totalSec; // kortere fil → sømløs loop
  node.connect(master);
  node.start(0);
  return ctx.startRendering();
}

/**
 * Mikser videoklippenes egen lyd (lavere volum) inn UNDER musikken for eksport.
 * Musikken spiller sammenhengende; hvert klipps lyd legges oppå ved sitt
 * tidsvindu (kappet til skjermtiden). Offline → deterministisk. Klipp uten
 * lydspor hoppes stille over.
 */
export async function mixClipAudioIntoMusic(
  music: AudioBuffer,
  clips: ReadonlyArray<{ url: string; startSec: number; slotSec: number }>,
  totalSec: number,
  clipVolume = 0.8, // klipp-lyd under musikken (eier-ønske: 80 %)
): Promise<AudioBuffer> {
  const frames = Math.ceil(totalSec * SAMPLE_RATE);
  const ctx = new OfflineAudioContext(2, frames, SAMPLE_RATE);
  const musicSrc = ctx.createBufferSource();
  musicSrc.buffer = music;
  musicSrc.connect(ctx.destination);
  musicSrc.start(0);
  for (const c of clips) {
    let buf: AudioBuffer | null = null;
    try {
      const res = await fetch(c.url);
      if (!res.ok) continue;
      buf = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch { continue; } // klipp uten lydspor / dekoding feilet
    if (!buf || buf.length === 0) continue;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = clipVolume;
    src.connect(g).connect(ctx.destination);
    const playDur = Math.min(c.slotSec, buf.duration);
    if (playDur > 0.02) src.start(c.startSec, 0, playDur);
  }
  return ctx.startRendering();
}

/** mp4-muxer-kompatibel minimums-interface (unngår hard typeavhengighet). */
interface AudioMuxerLike {
  addAudioChunk(chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void;
}

export const AAC_CONFIG: AudioEncoderConfig = {
  codec: 'mp4a.40.2',
  sampleRate: SAMPLE_RATE,
  numberOfChannels: 2,
  bitrate: 128_000,
};

/** Er AAC-encoding tilgjengelig? (Chromium: ja. Safari: varierer — da uten musikk.) */
export async function canEncodeAac(): Promise<boolean> {
  if (typeof AudioEncoder === 'undefined') return false;
  try {
    return (await AudioEncoder.isConfigSupported(AAC_CONFIG)).supported === true;
  } catch {
    return false;
  }
}

/**
 * Encoder AudioBufferen til AAC og skriver chunkene inn i muxeren.
 * Kalles ETTER at muxeren er opprettet med audio-config og FØR finalize().
 */
export async function encodeAacIntoMuxer(muxer: AudioMuxerLike, buffer: AudioBuffer): Promise<void> {
  let encodeError: unknown = null;
  const encoder = new AudioEncoder({
    output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
    error: e => { encodeError = e; },
  });
  encoder.configure(AAC_CONFIG);

  const CHUNK_FRAMES = 4800; // ~109 ms per AudioData
  const ch0 = buffer.getChannelData(0);
  const ch1 = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : ch0;

  for (let offset = 0; offset < buffer.length; offset += CHUNK_FRAMES) {
    if (encodeError) throw encodeError;
    const frames = Math.min(CHUNK_FRAMES, buffer.length - offset);
    const planar = new Float32Array(frames * 2);
    planar.set(ch0.subarray(offset, offset + frames), 0);
    planar.set(ch1.subarray(offset, offset + frames), frames);
    const data = new AudioData({
      format: 'f32-planar',
      sampleRate: SAMPLE_RATE,
      numberOfFrames: frames,
      numberOfChannels: 2,
      timestamp: Math.round((offset / SAMPLE_RATE) * 1e6),
      data: planar,
    });
    encoder.encode(data);
    data.close();
  }
  await encoder.flush();
  encoder.close();
  if (encodeError) throw encodeError;
}
